import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { IVideoClip, VideoEditConfig } from '@packages/global';
import { FFmpegCDNLoader } from './ffmpegCDNLoader';

export interface ExportProgress {
  stage: 'loading' | 'downloading' | 'processing' | 'finalizing' | 'complete';
  progress: number;
  message: string;
  currentClip?: number;
  totalClips?: number;
}

export class FFmpegExporter {
  private static sharedFFmpeg: FFmpeg | null = null;
  private static isSharedLoaded = false;
  private ffmpeg: FFmpeg | null = null;
  private isLoaded = false;
  private onProgress?: (progress: ExportProgress) => void;
  private cdnName?: string;

  constructor(onProgress?: (progress: ExportProgress) => void) {
    this.onProgress = onProgress;
  }

  /**
   * 加载 FFmpeg.wasm - 优先使用共享实例
   */
  async load(): Promise<void> {
    if (this.isLoaded) return;

    // 尝试使用共享实例
    if (FFmpegExporter.sharedFFmpeg && FFmpegExporter.isSharedLoaded) {
      this.ffmpeg = FFmpegExporter.sharedFFmpeg;
      this.isLoaded = true;
      console.log('[FFmpeg] 使用已加载的共享实例');
      return;
    }

    const loader = new FFmpegCDNLoader((loadProgress) => {
      this.updateProgress({
        stage: 'loading',
        progress: loadProgress.progress,
        message: loadProgress.message
      });
    });

    try {
      const { ffmpeg, cdnName } = await loader.loadFFmpeg();

      // 设置共享实例
      FFmpegExporter.sharedFFmpeg = ffmpeg;
      FFmpegExporter.isSharedLoaded = true;
      
      this.ffmpeg = ffmpeg;
      this.cdnName = cdnName;
      this.isLoaded = true;

      this.ffmpeg.on('log', ({ message }) => {
        console.log('[FFmpeg]', message);
      });

      console.log(`[FFmpeg] 成功加载，使用 CDN: ${cdnName}`);
    } catch (error) {
      console.error('[FFmpeg] 加载失败:', error);
      throw error;
    }
  }

  /**
   * 高性能导出视频和字幕 - 7步流程
   */
  async exportVideoWithSubtitles(
    videoClips: IVideoClip[],
    editConfig: VideoEditConfig,
    bgmUrl?: string
  ): Promise<{ videoBlob: Blob; subtitleContent: string }> {
    if (!this.ffmpeg || !this.isLoaded) {
      await this.load();
    }

    if (!this.ffmpeg) {
      throw new Error('FFmpeg 未初始化');
    }

    try {
      // Step 1: 下载资源
      await this.downloadResources(videoClips, editConfig, bgmUrl);

      // Step 2: 合成所有配音音频
      this.updateProgress({ stage: 'processing', progress: 20, message: '合成配音音频...' });
      const voiceoverAudio = await this.composeVoiceoverAudio(videoClips, editConfig);

      // Step 3: 合成配音和背景音乐
      this.updateProgress({ stage: 'processing', progress: 35, message: '合成背景音乐...' });
      const finalAudio = await this.composeBackgroundMusic(voiceoverAudio, bgmUrl, editConfig);

      // Step 4: 合成所有视频片段（消音）
      this.updateProgress({ stage: 'processing', progress: 50, message: '合成视频片段...' });
      const mutedVideo = await this.composeMutedVideoSegments(videoClips, editConfig);

      // Step 5: 合成最终音频和视频
      this.updateProgress({ stage: 'processing', progress: 70, message: '合成音视频...' });
      const finalVideo = await this.mergeAudioVideo(mutedVideo, finalAudio);

      // Step 6: 生成字幕文件
      this.updateProgress({ stage: 'processing', progress: 85, message: '生成字幕...' });
      const subtitleContent = this.generateSRTSubtitles(videoClips, editConfig);

      // Step 7: 读取最终视频
      this.updateProgress({ stage: 'finalizing', progress: 95, message: '正在生成最终视频...' });
      const data = await this.ffmpeg.readFile(finalVideo);
      const videoBlob = new Blob([data as any], { type: 'video/mp4' });

      this.updateProgress({ stage: 'complete', progress: 100, message: '导出完成！' });

      await this.cleanup();

      return { videoBlob, subtitleContent };
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  }

  /**
   * 下载所有需要的资源
   */
  private async downloadResources(
    videoClips: IVideoClip[],
    editConfig: VideoEditConfig,
    bgmUrl?: string
  ): Promise<void> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    this.updateProgress({
      stage: 'downloading',
      progress: 0,
      message: '正在下载资源...'
    });

    const totalResources = videoClips.length + (bgmUrl ? 1 : 0);
    let downloaded = 0;

    // 下载视频片段
    for (let i = 0; i < videoClips.length; i++) {
      const clip = videoClips[i];
      if (clip.videoUrl) {
        await this.ffmpeg.writeFile(
          `clip_${i}_video.mp4`,
          await fetchFile(clip.videoUrl)
        );
      }
      if (clip.scriptAudioUrl) {
        await this.ffmpeg.writeFile(
          `clip_${i}_audio.mp3`,
          await fetchFile(clip.scriptAudioUrl)
        );
      }
      // 注意：图片在 composeMutedVideoSegments 中下载，避免重复下载

      downloaded++;
      this.updateProgress({
        stage: 'downloading',
        progress: (downloaded / totalResources) * 100,
        message: `正在下载资源 (${downloaded}/${totalResources})...`
      });
    }

    // 下载背景音乐
    if (bgmUrl && editConfig.tracks.bgm.enabled) {
      await this.ffmpeg.writeFile('bgm.mp3', await fetchFile(bgmUrl));
      downloaded++;
      this.updateProgress({
        stage: 'downloading',
        progress: 100,
        message: '资源下载完成'
      });
    }
  }

  /**
   * 处理每个 clip - 简化版本（只处理视频轨道，跳过音频处理）
   */
  private async processClips(
    videoClips: IVideoClip[],
    editConfig: VideoEditConfig
  ): Promise<string[]> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    const processedClips: string[] = [];

    for (let i = 0; i < videoClips.length; i++) {
      const clip = videoClips[i];
      const clipConfig = editConfig.clips.find(c => c.clipId === clip.id);
      if (!clipConfig) continue;

      this.updateProgress({
        stage: 'processing',
        progress: (i / videoClips.length) * 100,
        message: `正在剪切片段 ${i + 1}/${videoClips.length}...`,
        currentClip: i + 1,
        totalClips: videoClips.length
      });

      const outputFile = `processed_${i}.mp4`;

      // ⭐ 简化：只做视频轨道的时间裁剪，使用 -c copy（最快）
      await this.fastTrimClip(i, clip, clipConfig, outputFile);

      processedClips.push(outputFile);
    }

    return processedClips;
  }

  /**
   * 快速裁剪 - 不重新编码（速度提升30-40倍）
   */
  private async fastTrimClip(
    index: number,
    clip: IVideoClip,
    clipConfig: any,
    outputFile: string
  ): Promise<void> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    const inputVideo = `clip_${index}_video.mp4`;
    const args: string[] = [];

    // ⭐ -ss 放在 -i 前面，利用关键帧快速定位
    if (clipConfig.trimStart !== undefined) {
      args.push('-ss', clipConfig.trimStart.toString());
    }

    args.push('-i', inputVideo);

    if (clipConfig.trimStart !== undefined && clipConfig.trimEnd !== undefined) {
      args.push('-t', (clipConfig.trimEnd - clipConfig.trimStart).toString());
    }

    // ⭐ 关键：使用 -c copy 不重新编码（无损、超快）
    args.push('-c', 'copy');
    args.push('-avoid_negative_ts', '1');
    args.push(outputFile);

    await this.ffmpeg.exec(args);
  }

  /**
   * 时间裁剪 + 音频处理 - 只重新编码音频（速度提升15-20倍）
   */
  private async trimAndProcessAudio(
    index: number,
    clip: IVideoClip,
    clipConfig: any,
    outputFile: string
  ): Promise<void> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    const inputVideo = `clip_${index}_video.mp4`;
    const inputAudio = `clip_${index}_audio.mp3`;
    const args: string[] = [];

    // 时间定位
    if (clipConfig.trimStart !== undefined) {
      args.push('-ss', clipConfig.trimStart.toString());
    }

    args.push('-i', inputVideo);

    if (clip.scriptAudioUrl) {
      args.push('-i', inputAudio);
    }

    if (clipConfig.trimStart !== undefined && clipConfig.trimEnd !== undefined) {
      args.push('-t', (clipConfig.trimEnd - clipConfig.trimStart).toString());
    }

    // 音频处理
    if (clip.scriptAudioUrl) {
      const videoVolume = clipConfig.videoVolume ?? 0;
      const dialogueVolume = clipConfig.dialogueVolume ?? 1;

      args.push(
        '-filter_complex',
        `[0:a]volume=${videoVolume}[a0];[1:a]volume=${dialogueVolume}[a1];[a0][a1]amix=inputs=2:duration=shortest[aout]`
      );
      args.push('-map', '0:v');
      args.push('-map', '[aout]');
    } else if (clipConfig.videoVolume !== undefined && clipConfig.videoVolume !== 1) {
      args.push('-af', `volume=${clipConfig.videoVolume}`);
    }

    // ⭐ 视频不重新编码，只编码音频（保持视频质量，快速）
    args.push('-c:v', 'copy');
    args.push('-c:a', 'aac');
    args.push('-b:a', '192k');
    args.push(outputFile);

    await this.ffmpeg.exec(args);
  }

  /**
   * 完全重新编码 - 用于字幕等需要视频处理的场景
   */
  private async fullProcessClip(
    index: number,
    clip: IVideoClip,
    clipConfig: any,
    outputFile: string
  ): Promise<void> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    const inputVideo = `clip_${index}_video.mp4`;
    const inputAudio = `clip_${index}_audio.mp3`;
    const args: string[] = [];

    // 输入视频
    if (clip.videoUrl) {
      args.push('-i', inputVideo);
    }

    // 输入音频
    if (clip.scriptAudioUrl) {
      args.push('-i', inputAudio);
    }

    // 裁剪视频
    if (clipConfig.trimStart !== undefined && clipConfig.trimEnd !== undefined) {
      args.push('-ss', clipConfig.trimStart.toString());
      args.push('-t', (clipConfig.trimEnd - clipConfig.trimStart).toString());
    }

    // 音频处理
    if (clip.scriptAudioUrl) {
      const videoVolume = clipConfig.videoVolume ?? 0;
      const dialogueVolume = clipConfig.dialogueVolume ?? 1;

      args.push(
        '-filter_complex',
        `[0:a]volume=${videoVolume}[a0];[1:a]volume=${dialogueVolume}[a1];[a0][a1]amix=inputs=2:duration=shortest[aout]`
      );
      args.push('-map', '0:v');
      args.push('-map', '[aout]');
    } else {
      const videoVolume = clipConfig.videoVolume ?? 0;
      args.push('-filter:a', `volume=${videoVolume}`);
    }

    // 字幕处理
    if (clipConfig.showSubtitle && clip.script) {
      // TODO: 添加字幕烧录逻辑
    }

    // 输出设置 - 完全重新编码
    args.push('-c:v', 'libx264');
    args.push('-preset', 'fast');
    args.push('-crf', '23');
    args.push('-c:a', 'aac');
    args.push('-b:a', '192k');
    args.push(outputFile);

    await this.ffmpeg.exec(args);
  }

  /**
   * Step 2: 合成所有配音音频
   */
  private async composeVoiceoverAudio(videoClips: IVideoClip[], editConfig: VideoEditConfig): Promise<string> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    const audioSegments: string[] = [];

    for (let i = 0; i < videoClips.length; i++) {
      const clip = videoClips[i];
      const clipConfig = editConfig.clips.find(c => c.clipId === clip.id);
      if (!clipConfig) continue;

      const segmentFile = `audio_segment_${i}.wav`;

      const videoTrackVolume = editConfig.tracks.video.enabled
        ? editConfig.tracks.video.volume ?? 1
        : 0;
      const audioTrackVolume = editConfig.tracks.audio.enabled
        ? editConfig.tracks.audio.volume ?? 1
        : 0;
      const finalVideoVolume = (clipConfig.videoVolume ?? 0) * videoTrackVolume;
      const finalDialogueVolume = (clipConfig.dialogueVolume ?? 1) * audioTrackVolume;

      if (finalVideoVolume > 0 && clip.videoUrl) {
        // 使用视频音频
        await this.ffmpeg.exec([
          '-i', `clip_${i}_video.mp4`,
          '-t', clipConfig.duration.toString(),
          '-af', `volume=${finalVideoVolume}`,
          '-c:a', 'pcm_s16le',
          segmentFile
        ]);
      } else if (finalDialogueVolume > 0 && clip.scriptAudioUrl) {
        // 使用配音音频
        await this.ffmpeg.exec([
          '-i', `clip_${i}_audio.mp3`,
          '-t', clipConfig.duration.toString(),
          '-af', `volume=${finalDialogueVolume}`,
          '-c:a', 'pcm_s16le',
          segmentFile
        ]);
      } else {
        // 生成空白音频
        await this.ffmpeg.exec([
          '-f', 'lavfi',
          '-i', `anullsrc=channel_layout=stereo:sample_rate=44100`,
          '-t', clipConfig.duration.toString(),
          '-c:a', 'pcm_s16le',
          segmentFile
        ]);
      }

      audioSegments.push(segmentFile);
    }

    // 拼接所有音频段
    const concatList = audioSegments.map(file => `file '${file}'`).join('\n');
    await this.ffmpeg.writeFile('audio_concat.txt', concatList);

    await this.ffmpeg.exec([
      '-f', 'concat',
      '-safe', '0',
      '-i', 'audio_concat.txt',
      '-c', 'copy',
      'voiceover_audio.wav'
    ]);

    return 'voiceover_audio.wav';
  }

  /**
   * Step 3: 合成配音和背景音乐
   */
  private async composeBackgroundMusic(voiceoverAudio: string, bgmUrl?: string, editConfig?: VideoEditConfig): Promise<string> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    if (!bgmUrl || !editConfig?.tracks.bgm.enabled) {
      return voiceoverAudio;
    }

    const bgmVolume = editConfig.tracks.bgm.volume || 0.3;
    const totalDuration = editConfig.timeline.totalDuration;

    // 裁切背景音乐并添加淡出效果
    await this.ffmpeg.exec([
      '-i', 'bgm.mp3',
      '-t', totalDuration.toString(),
      '-af', `volume=${bgmVolume},afade=t=out:st=${Math.max(0, totalDuration - 3)}:d=3`,
      '-c:a', 'pcm_s16le',
      'bgm_processed.wav'
    ]);

    // 合成配音和背景音乐
    await this.ffmpeg.exec([
      '-i', voiceoverAudio,
      '-i', 'bgm_processed.wav',
      '-filter_complex', '[0:a][1:a]amix=inputs=2:duration=first[aout]',
      '-map', '[aout]',
      '-c:a', 'pcm_s16le',
      'final_audio.wav'
    ]);

    return 'final_audio.wav';
  }

  /**
   * Step 4: 合成所有视频片段（消音）
   */
  private async composeMutedVideoSegments(videoClips: IVideoClip[], editConfig: VideoEditConfig): Promise<string> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    const mutedClips: string[] = [];

    for (let i = 0; i < videoClips.length; i++) {
      const clip = videoClips[i];
      const clipConfig = editConfig.clips.find(c => c.clipId === clip.id);
      if (!clipConfig) continue;

      const mutedFile = `muted_${i}.mp4`;

      if (clip.videoUrl) {
        // 有视频：消音并裁切
        const args = [];
        if (clipConfig.trimStart) {
          args.push('-ss', clipConfig.trimStart.toString());
        }
        args.push('-i', `clip_${i}_video.mp4`);
        if (clipConfig.trimEnd && clipConfig.trimStart) {
          args.push('-t', (clipConfig.trimEnd - clipConfig.trimStart).toString());
        } else {
          args.push('-t', clipConfig.duration.toString());
        }
        args.push('-an', '-c:v', 'copy', mutedFile);

        await this.ffmpeg.exec(args);
      } else if (clip.startImage) {
        // 有图片：生成静态视频 - 保持比例
        await this.ffmpeg.writeFile(`image_${i}.jpg`, await fetchFile(clip.startImage));
        await this.ffmpeg.exec([
          '-loop', '1',
          '-i', `image_${i}.jpg`,
          '-t', clipConfig.duration.toString(),
          '-c:v', 'libx264',
          '-preset', 'ultrafast',
          '-crf', '28',
          '-pix_fmt', 'yuv420p',
          '-r', '1',
          '-vf', 'scale=1280:720:force_original_aspect_ratio=decrease,pad=1280:720:(ow-iw)/2:(oh-ih)/2:black,fps=30',
          mutedFile
        ]);
      } else {
        // 无素材：生成黑屏视频
        await this.ffmpeg.exec([
          '-f', 'lavfi',
          '-i', 'color=c=black:s=1280x720:r=30',
          '-t', clipConfig.duration.toString(),
          '-pix_fmt', 'yuv420p',
          mutedFile
        ]);
      }

      mutedClips.push(mutedFile);
    }

    // 拼接所有消音视频
    const concatList = mutedClips.map(file => `file '${file}'`).join('\n');
    await this.ffmpeg.writeFile('video_concat.txt', concatList);

    await this.ffmpeg.exec([
      '-f', 'concat',
      '-safe', '0',
      '-i', 'video_concat.txt',
      '-c', 'copy',
      'muted_video.mp4'
    ]);

    return 'muted_video.mp4';
  }

  /**
   * Step 5: 合成最终音频和视频
   */
  private async mergeAudioVideo(videoFile: string, audioFile: string): Promise<string> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    await this.ffmpeg.exec([
      '-i', videoFile,
      '-i', audioFile,
      '-c:v', 'copy',
      '-c:a', 'aac',
      '-b:a', '192k',
      '-shortest',
      'final_video.mp4'
    ]);

    return 'final_video.mp4';
  }

  /**
   * Step 6: 生成SRT字幕文件
   */
  private generateSRTSubtitles(videoClips: IVideoClip[], editConfig: VideoEditConfig): string {
    let srtContent = '';
    let subtitleIndex = 1;

    editConfig.clips.forEach((clipInfo) => {
      const clip = videoClips.find(c => c.id === clipInfo.clipId);
      if (clip && clipInfo.showSubtitle && clip.script) {
        const startTime = this.formatSRTTime(clipInfo.startTime);
        const endTime = this.formatSRTTime(clipInfo.startTime + clipInfo.duration);

        srtContent += `${subtitleIndex}\n`;
        srtContent += `${startTime} --> ${endTime}\n`;
        srtContent += `${clip.script}\n\n`;
        subtitleIndex++;
      }
    });

    return srtContent;
  }

  /**
   * 格式化SRT时间
   */
  private formatSRTTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
  }

  /**
   * 添加背景音乐
   */
  private async addBackgroundMusic(
    videoFile: string,
    bgmUrl: string,
    editConfig: VideoEditConfig
  ): Promise<string> {
    if (!this.ffmpeg) throw new Error('FFmpeg not initialized');

    this.updateProgress({
      stage: 'processing',
      progress: 90,
      message: '正在添加背景音乐...'
    });

    const bgmVolume = editConfig.tracks.bgm.volume || 0.3;

    await this.ffmpeg.exec([
      '-i', videoFile,
      '-i', 'bgm.mp3',
      '-filter_complex',
      `[0:a]volume=1[a0];[1:a]volume=${bgmVolume},aloop=loop=-1:size=2e+09[a1];[a0][a1]amix=inputs=2:duration=first[aout]`,
      '-map', '0:v',
      '-map', '[aout]',
      '-c:v', 'copy',
      '-c:a', 'aac',
      '-b:a', '192k',
      '-shortest',
      'final.mp4'
    ]);

    return 'final.mp4';
  }

  /**
   * 清理临时文件
   */
  private async cleanup(): Promise<void> {
    if (!this.ffmpeg) return;

    try {
      const files = await this.ffmpeg.listDir('/');
      for (const file of files) {
        if (file.name.endsWith('.mp4') || file.name.endsWith('.mp3') || file.name.endsWith('.txt')) {
          try {
            await this.ffmpeg.deleteFile(file.name);
          } catch (e) {
            // 忽略删除错误
          }
        }
      }
    } catch (error) {
      console.warn('Cleanup failed:', error);
    }
  }

  /**
   * 更新进度
   */
  private updateProgress(progress: ExportProgress): void {
    if (this.onProgress) {
      this.onProgress(progress);
    }
  }

  /**
   * 终止导出 - 不销毁共享实例
   */
  async terminate(): Promise<void> {
    // 不销毁共享实例，只清理当前引用
    this.ffmpeg = null;
    this.isLoaded = false;
    console.log('[FFmpeg] 导出器已终止，保留共享实例');
  }

  /**
   * 静态方法：强制销毁共享实例（仅在必要时使用）
   */
  static async destroySharedInstance(): Promise<void> {
    if (FFmpegExporter.sharedFFmpeg) {
      await FFmpegExporter.sharedFFmpeg.terminate();
      FFmpegExporter.sharedFFmpeg = null;
      FFmpegExporter.isSharedLoaded = false;
      console.log('[FFmpeg] 共享实例已销毁');
    }
  }
}
